import MenuIcon from "@mui/icons-material/Menu";
import {
  Badge,
  Chip,
  CircularProgress,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Slide from "@mui/material/Slide";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import { useTheme } from "@mui/styles";
import { ReactComponent as CartIcon } from "assets/header/cart.svg";
import commonConfig from "config/commonConfig";
import { AppDivider } from "elements/divider";
import { motion } from "framer-motion";
import { get, isEmpty, isUndefined, sortBy, uniqueId } from "lodash";
import Map from "lodash/map";
import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineHeart, AiOutlinePoweroff } from "react-icons/ai";
import { PiPackage } from "react-icons/pi";
import { useLocation, useNavigate } from "react-router-dom";
import { searchUserCart } from "rest/rest";
import { useAppApi } from "store/api-context";
import { useAppAuth } from "store/auth-context";
import { getMainClient } from "utils/common/common.utils";
import { ROUTES, ROUTE_SLUGS } from "utils/constants/route.constants";
import {
  commonHoverProps,
  commonTapProps,
  liVariants,
  ulVariants,
} from "utils/framer-animations/framer";
import localStorageUtils, {
  localStorageKeys,
} from "utils/local-utils/localstorage.utils";
import { errorToast } from "utils/toaster/toaster.util";
import downArrow from "../../assets/header/down-arrow.svg";
import ProfileIconMobile from "../../assets/header/profile.svg";
import MenuHover from "./MenuHover.component";
import { Icons, drawerWidth, navItems } from "./data";
import "./header.style.css";
import Notification from "./notification/notification.component";
import { HeaderLinks } from "./style";

function HideOnScroll(props) {
  const { children, window } = props;

  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={true} direction="down" in={!trigger}>
      {React.cloneElement(children, { trigger })}
    </Slide>
  );
}

export default function Header(props) {
  const { window, appBarStyles, isDivisionPath, appBarRef } = props;
  const theme = useTheme();
  const mainClient = getMainClient(commonConfig);

  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDetailLink, setOpenDetailLink] = useState(false);
  const { pathname } = useLocation();
  const [isWishListLoading, setIsWishListLoading] = useState(false);
  const [isCartListLoading, setIsCartListLoading] = useState(false);

  const navigate = useNavigate();
  const { appLogout, isAuthenticated, userDetails } = useAppAuth();

  const {
    isCartItemsAdded,
    setSelectedDivision,
    wishListItems,
    cartItemItems,
  } = useAppApi();

  const [navigationItems, setNavigationItems] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);

  const isHomePage = pathname === ROUTES.HOME;
  const isDivisionPage =
    pathname.includes("division") || pathname.includes("overview");

  const {
    fetchDivisions,
    state,
    setCartItemItems,
    fetchWishlist,
    setWishlistItems,
  } = useAppApi();

  const { divisions } = state;

  const fullName = useMemo(
    () =>
      `${get(userDetails, "firstName", "")} ${get(
        userDetails,
        "lastName",
        ""
      )?.substr(0, 1)}`,

    []
  );

  const isDivisionControlled = get(mainClient, "isDivisionControlled", false);
  const clientName = get(mainClient, "name", "");

  const open = Boolean(anchorEl);

  const handleProfile = () => {
    navigate(ROUTES.AUTH_TYPE.replace(ROUTE_SLUGS.authType, "login"));
  };

  const handleClick = (event) => {
    if (isAuthenticated) {
      setAnchorEl(event.currentTarget);
    } else {
      handleProfile();
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    appLogout();
    handleClose();
  };

  const handleOnClickYourOrders = () => {
    navigate(ROUTES.YOUR_ORDERS);
    handleClose();
    handleDrawerToggle();
  };

  useEffect(() => {
    setNavigationItems([
      ...navItems,
      isDivisionControlled
        ? {
            title: "Division",
            link: [
              ...divisions.map((div) => ({
                title: div?.name,
                link: `/division/${div?._id}`,
                division: div,
              })),
            ],
            index: 2,
          }
        : null,
    ]);
  }, [divisions, isDivisionControlled]);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const handleDropDownClick = (isOpened, division) => {
    setOpenDetailLink(isOpened);
    setSelectedDivision(division);
  };

  const callOnLoadApis = async () => {
    const userDetails = localStorageUtils?.get(localStorageKeys?.userDetails);
    setIsCartListLoading(true);
    try {
      const userCartResponse = await searchUserCart(userDetails?._id);
      if (userCartResponse) {
        setCartItemItems(userCartResponse);

        try {
          setIsWishListLoading(true);
          fetchWishlist();
        } catch (err) {
          throw err;
        } finally {
          setIsWishListLoading(false);
        }
      }
    } catch (error) {
      errorToast(error?.response?.data?.message);
    } finally {
      setIsCartListLoading(false);
    }
  };
  useEffect(() => {
    fetchDivisions();
  }, [location.pathname]);

  useEffect(() => {
    callOnLoadApis();
  }, []);

  if (isDivisionPath) {
    return null;
  }
  if (pathname === ROUTES.DELETE_USER || pathname === ROUTES.PRIVACY_POLICY) {
    return null;
  }

  const drawer = (
    <Box sx={{ textAlign: "center" }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: 600 }}>
        {clientName}
      </Typography>
      <Divider />

      <Typography sx={{ mx: 3, display: "flex", my: 1, fontWeight: 600 }}>
        Hello, {!isEmpty(fullName) ? fullName : "Guest"}
      </Typography>

      <List>
        <motion.ul variants={ulVariants}></motion.ul>
        {Map(
          sortBy(
            navigationItems.filter((item) => item),
            [(o) => o.index]
          ),
          ({ title, link, notVisible }) =>
            typeof link === "object" ? (
              <>
                <ListItem key={uniqueId()}>
                  <details>
                    <summary>Divisions</summary>
                    {Map(link, (item) => (
                      <ListItem>
                        <motion.li
                          className="cursor-pointer"
                          variants={liVariants}
                          key={item?.title}
                          onClick={() => {
                            setSelectedDivision(item?.division);
                            navigate(item?.link);
                            handleDrawerToggle();
                          }}
                          whileHover={{
                            scale: 1.1,
                            borderBottom: "1px solid red",
                            cursor: "pointer",
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <ListItemText>{item?.title}</ListItemText>
                        </motion.li>
                      </ListItem>
                    ))}
                  </details>
                </ListItem>
              </>
            ) : notVisible.includes(location.pathname) ? null : (
              <ListItem
                key={uniqueId()}
                sx={{
                  textAlign: "center",
                  paddingLeft: "1.4rem",
                }}
              >
                <motion.li
                  className="cursor-pointer"
                  variants={liVariants}
                  whileHover={{
                    scale: 1.1,
                    borderBottom: "1px solid red",
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    handleDrawerToggle();
                    navigate(link);
                  }}
                >
                  {title}
                </motion.li>
              </ListItem>
            )
        )}
        <AppDivider />

        <ListItem
          sx={{
            textAlign: "center",
            paddingLeft: "1.4rem",
          }}
        >
          {isAuthenticated ? (
            <Box
              className="cursor-pointer"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                width: "100%",
              }}
            >
              <Stack
                onClick={() => {
                  handleDrawerToggle();
                  navigate(ROUTES.WISH_LIST);
                }}
                direction="row"
                alignItems="center"
                gap="0.8rem"
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap="0.8rem"
                  justifyContent="flex-start"
                >
                  <AiOutlineHeart />
                  <Typography>Your wishlist</Typography>
                </Stack>
                <Chip
                  label={wishListItems?.items?.length}
                  color="error"
                  size="small"
                />
              </Stack>

              <Stack
                onClick={handleOnClickYourOrders}
                direction="row"
                alignItems="center"
                gap="0.8rem"
                justifyContent="flex-start"
                sx={{
                  width: "100%",
                }}
              >
                <PiPackage />
                <Typography>Your orders</Typography>
              </Stack>

              <Stack
                onClick={handleLogout}
                direction="row"
                alignItems="center"
                gap="0.8rem"
                justifyContent="flex-start"
              >
                <AiOutlinePoweroff />
                <Typography>Logout</Typography>
              </Stack>
            </Box>
          ) : (
            <Box className="cursor-pointer">
              <Typography onClick={handleProfile}>Login</Typography>
            </Box>
          )}
        </ListItem>
      </List>
    </Box>
  );

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const division = localStorageUtils?.get(localStorageKeys.division);

  const otherProps = {
    ...props,
    setSticky: (hel) => console.log(hel, "Hello"),
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <HideOnScroll {...otherProps}>
        <AppBar
          ref={appBarRef}
          component="nav"
          sx={{
            background: {
              xs: "black",
              md: "transparent",
            },
            boxShadow: "none",
            padding: {
              sm: "0 2rem",
              md: isDivisionPage ? "0rem 8rem" : "0 4rem",
            },
            margin:
              isAuthenticated && isDivisionPage
                ? "-0.8rem 0"
                : {
                    xs: 0,
                    md: "1rem 0",
                  },
            // position: "fixed", // Set position to sticky
            // top: 0, // Stick to the top of the viewport
            // background: "black",
            // margin: 0,
            top: {
              xs: isDivisionPage ? 12 : 0,
              md: isDivisionPage ? "4rem" : "1rem",
            },
            ...appBarStyles,
          }}
        >
          <Toolbar>
            <Box
              sx={{
                display: { sm: "none", xs: "flex" },
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  mr: 1,
                  display: { sm: "none" },
                  background: !isHomePage
                    ? theme?.palette?.colors?.brandColorPrimary
                    : theme?.palette?.colors?.background,
                  color: isHomePage
                    ? theme?.palette?.colors?.brandColorPrimary
                    : theme?.palette?.colors?.background,
                }}
              >
                <MenuIcon />
              </IconButton>
              <Stack
                direction="row"
                sx={{
                  marginRight: "14px",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  onClick={() => {
                    navigate("/cart");
                  }}
                  className="cursor-pointer"
                >
                  <Badge
                    badgeContent={cartItemItems?.items?.length}
                    color="error"
                  >
                    <CartIcon />
                  </Badge>
                </Box>
                <Notification
                  isDivisionPage={isDivisionPage}
                  isHomePage={isHomePage}
                  styles={{
                    display: { sm: "none" },
                  }}
                />
              </Stack>
            </Box>
            <Box
              sx={{
                height: "60px",
                display: { xs: "none", sm: "flex" },
                justifyContent: "center",
                flex: "1",
                bgcolor: "transparent",
                color: isHomePage || isDivisionPage ? "white" : "#004081",
                marginTop: "0px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flex: "33.33%",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {!isUndefined(navigationItems) &&
                  Map(
                    sortBy(
                      navigationItems.filter((item) => item),
                      [(o) => o.index]
                    ),
                    ({ title, link, notVisible }) =>
                      typeof link === "object" ? (
                        <div
                          key={uniqueId()}
                          className="cursor-pointer"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            position: "relative",
                            marginLeft: "10px",
                          }}
                          onClick={() => setOpenDetailLink(!openDetailLink)}
                        >
                          <span
                            className="cursor-pointer"
                            style={{
                              color:
                                isHomePage || isDivisionPage
                                  ? "white"
                                  : "#004081",
                              textTransform: "capitalize",
                              textDecoration: "none",
                              fontSize: theme.spacing(2),
                            }}
                          >
                            {division?.name && !isHomePage
                              ? division?.name
                              : title}{" "}
                          </span>
                          <img
                            src={downArrow}
                            alt={"dropdown"}
                            width={10}
                            height={10}
                            style={{
                              marginTop: "4px",
                              marginLeft: "10px",
                              filter:
                                isHomePage || isDivisionPage
                                  ? "unset"
                                  : "invert(37%) sepia(53%) saturate(4818%) hue-rotate(200deg) brightness(89%) contrast(101%)",
                              "@media (width <= 768px)": {
                                marginLeft: "12px",
                              },
                            }}
                          />
                          {openDetailLink && (
                            <motion.ul
                              onMouseLeave={() => {
                                setOpenDetailLink(!openDetailLink);
                              }}
                              style={{
                                position: "absolute",
                                top: "30px",
                                width: "200px",
                                textAlign: "left",
                                left: 0,
                                background:
                                  isHomePage || isDivisionPage
                                    ? "rgba(0,0,0, 0.5)"
                                    : "white",
                                listStyleType: "none",
                                border: "1px solid gray",
                                borderRadius: "8px",
                                padding: "10px",
                                margin: 0,
                              }}
                            >
                              {Map(link, (item) => (
                                <motion.li
                                  key={item.title}
                                  onClick={() =>
                                    handleDropDownClick(false, item?.division)
                                  }
                                  whileHover={{
                                    ...commonHoverProps,
                                    textDecoration: "underline",
                                  }}
                                  whileTap={{
                                    ...commonTapProps,
                                  }}
                                >
                                  <HeaderLinks
                                    state={{
                                      isHomePage,
                                      isDivisionPage,
                                      text: item.title,
                                    }}
                                    to={item.link}
                                  >
                                    {item.title}
                                  </HeaderLinks>
                                </motion.li>
                              ))}
                            </motion.ul>
                          )}
                        </div>
                      ) : notVisible.includes(location.pathname) ? null : (
                        <HeaderLinks
                          key={uniqueId()}
                          state={{ isHomePage, isDivisionPage }}
                          to={link}
                        >
                          <span onClick={() => setOpenDetailLink(false)}>
                            {title}
                          </span>
                        </HeaderLinks>
                      )
                  )}
              </div>

              <div
                style={{
                  display: "flex",
                  flex: "33.33%",
                  justifyContent: "center",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <h3 style={{ margin: 0 }}>{clientName?.split(" ")[0]}</h3>
                <h3 style={{ margin: 0 }}>{clientName?.split(" ")[1]}</h3>
              </div>
              <div
                style={{
                  display: "flex",
                  flex: "33.33%",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "4rem",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", gap: "0.6rem" }}>
                    {Map(Icons, ({ src, alt, filledIcon }) => (
                      <div className="header-image_container" key={uniqueId()}>
                        <Badge
                          badgeContent={
                            isWishListLoading || isCartListLoading ? (
                              <CircularProgress
                                size={6}
                                sx={{ color: "#fff" }}
                              />
                            ) : alt === "wishlist" ? (
                              wishListItems?.items?.length
                            ) : (
                              cartItemItems?.items?.length
                            )
                          }
                          color="error"
                        >
                          <img
                            className="cursor-pointer"
                            onClick={() => {
                              if (alt === "wishlist") {
                                navigate(ROUTES.WISH_LIST);
                              }

                              if (alt === "cart") {
                                navigate(ROUTES.CART);
                              }
                            }}
                            src={src}
                            alt={alt}
                            height={24}
                            width={24}
                            style={{
                              filter:
                                isHomePage || isDivisionPage
                                  ? "unset"
                                  : "invert(37%) sepia(53%) saturate(4818%) hue-rotate(200deg) brightness(89%) contrast(101%)",
                            }}
                          />
                        </Badge>
                      </div>
                    ))}
                  </div>

                  <div
                    className="flex align-center"
                    style={{
                      gap: "10px",
                    }}
                  >
                    <Notification
                      styles={{
                        display: { sm: "flex" },
                      }}
                    />
                    <div className="flex align-center">
                      <img
                        onClick={handleClick}
                        className="cursor-pointer"
                        src={ProfileIconMobile}
                        alt="profile-icon"
                        height={24}
                        width={24}
                        style={{
                          marginRight: "4px",
                          filter:
                            isHomePage || isDivisionPage
                              ? "unset"
                              : "invert(37%) sepia(53%) saturate(4818%) hue-rotate(200deg) brightness(89%) contrast(101%)",
                        }}
                      />
                      <Typography>{fullName}</Typography>
                      <MenuHover
                        open={open}
                        handleClose={handleClose}
                        anchorEl={anchorEl}
                        handleOnClickYourOrders={handleOnClickYourOrders}
                        handleLogout={handleLogout}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <Box component="nav">
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}
